import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import getAppSettings from './queryFunctions/getAppSettings';
import {
  updateAutoLoadType,
  updateDateFormat,
  updateHistoryFetchLimit,
  updateLastLoadedDatasetId,
  updateLocale,
  updateWeekStartsOn,
} from './queryFunctions/setAppSettings';
import { queryClient } from '../setupTanstackQuery';
import { AppSettings } from '../types/AppSettings';
import {
  LOCAL_STORAGE_DATEFORMAT_KEY,
  LOCAL_STORAGE_HISTORY_DATA_FETCH_LIMIT_KEY,
  LOCAL_STORAGE_LOCALE_KEY,
  LOCAL_STORAGE_WEEK_STARTS_ON_KEY,
} from '../config';
import useSubscription from './useSubscription';

function useAppSettings() {
  const subscriptionQuery = useSubscription();

  const appSettingsQuery = useQuery({
    queryKey: ['app-settings'],
    queryFn: getAppSettings,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: subscriptionQuery.isSuccess,
  });

  const updateAutoLoadTypeMutation = useMutation({
    mutationFn: updateAutoLoadType,
    onSuccess: (_, newValue) => {
      queryClient.setQueryData(['app-settings'], (prevState: AppSettings) => {
        if (!prevState) return prevState;
        return {
          ...prevState,
          startupMode: newValue,
        };
      });
    },
  });

  const updateDateFormatMutation = useMutation({
    mutationFn: updateDateFormat,
    onSuccess: (_, newValue) => {
      queryClient.setQueryData(['app-settings'], (prevState: AppSettings) => {
        if (!prevState) return prevState;
        localStorage.setItem(LOCAL_STORAGE_DATEFORMAT_KEY, newValue);
        return {
          ...prevState,
          dateFormat: newValue,
        };
      });
    },
  });

  const updateLastLoadedDatasetIdMutation = useMutation({
    mutationFn: updateLastLoadedDatasetId,
    onSuccess: (_, newValue) => {
      queryClient.setQueryData(['app-settings'], (prevState: AppSettings) => {
        if (!prevState) return prevState;
        return {
          ...prevState,
          lastLoadedDatasetId: newValue,
        };
      });
    },
  });

  const updateLocaleMutation = useMutation({
    mutationFn: updateLocale,
    onSuccess: (_, newValue) => {
      queryClient.setQueryData(['app-settings'], (prevState: AppSettings) => {
        if (!prevState) return prevState;
        localStorage.setItem(LOCAL_STORAGE_LOCALE_KEY, newValue);
        return {
          ...prevState,
          locale: newValue,
        };
      });
    },
  });

  const updateWeekStartsOnMutation = useMutation({
    mutationFn: updateWeekStartsOn,
    onSuccess: (_, newValue) => {
      queryClient.setQueryData(['app-settings'], (prevState: AppSettings) => {
        if (!prevState) return prevState;
        localStorage.setItem(LOCAL_STORAGE_WEEK_STARTS_ON_KEY, String(newValue));
        return {
          ...prevState,
          weekStartsOn: newValue,
        };
      });
    },
  });

  const updateHistoryFetchLimitMutation = useMutation({
    mutationFn: updateHistoryFetchLimit,
    onSuccess: (_, newValue) => {
      queryClient.setQueryData(['app-settings'], (prevState: AppSettings) => {
        if (!prevState) return prevState;
        localStorage.setItem(LOCAL_STORAGE_HISTORY_DATA_FETCH_LIMIT_KEY, String(newValue));
        return {
          ...prevState,
          historicalDataFetchLimit: newValue,
        };
      });
    },
  });

  return {
    ...appSettingsQuery,
    updateAutoLoadType: updateAutoLoadTypeMutation.mutate,
    updateDateFormat: updateDateFormatMutation.mutate,
    updateHistoryFetchLimit: updateHistoryFetchLimitMutation.mutate,
    updateLastLoadedDatasetId: updateLastLoadedDatasetIdMutation.mutate,
    updateLocale: updateLocaleMutation.mutate,
    updateWeekStartsOn: updateWeekStartsOnMutation.mutate,
  };
}

export default useAppSettings;
